import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { StudentsWorkloadAchievementParams } from 'domain/models';
import {
  ExportStudentsWorkloadAchievementUseCaseInterface,
  GetStudentsWorkloadAchievementUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { Option } from 'presentation/app/models/option';
import { useCallback, useRef, useState } from 'react';
import * as Yup from 'yup';

type StudentsWorkloadAchievementFormValues = {
  entity: Option | null;
  course: Option | null;
  unit: Option | null;
  semester: Option | null;
  groups: Option | null;
  starts: string | null;
  ends: string | null;
};

type ExportType =
  ExportStudentsWorkloadAchievementUseCaseInterface.Params['type'];

const MAX_GROUPS_PAGE_LENGTH = 100;

export const useStudentsWorkloadAchievement = () => {
  const { reportService } = useService();

  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [tableLoading, setTableLoading] = useState(STATUSPAGE.DONE);
  const [buttonsLoading, setButtonsLoading] = useState(STATUSPAGE.DONE);
  const [loadingAllGroups, setLoadingAllGroups] = useState(false);
  const [workloadAchievementData, setWorkloadAchievementData] =
    useState<GetStudentsWorkloadAchievementUseCaseInterface.Result>({
      total: 0,
      groups: [],
    });

  const buildParams = (values: StudentsWorkloadAchievementFormValues) => ({
    starts: values.starts || undefined,
    ends: values.ends || undefined,
    institutionHashId: values.entity?.value,
    courseHashId: values.course?.value,
    semesterHashId: values.semester?.value,
    curricularUnitHashId: values.unit?.value,
    groupHashId: values.groups?.value,
  });

  const requestGetStudentsWorkloadAchievement = useCallback(
    async (
      params: StudentsWorkloadAchievementParams,
      options?: { shouldUpdateButtonsLoading?: boolean },
    ) => {
      setTableLoading(STATUSPAGE.LOADING);
      setLoadingAllGroups(true);

      if (options?.shouldUpdateButtonsLoading) {
        setButtonsLoading(STATUSPAGE.LOADING);
      }

      let firstPage: GetStudentsWorkloadAchievementUseCaseInterface.Result;

      try {
        firstPage = await reportService.getStudentsWorkloadAchievement({
          ...params,
          page: 1,
          length: MAX_GROUPS_PAGE_LENGTH,
        });

        setWorkloadAchievementData(firstPage);
      } catch (err) {
        setWorkloadAchievementData({ total: 0, groups: [] });
        notifyError(err.message);
        setTableLoading(STATUSPAGE.DONE);
        setLoadingAllGroups(false);

        if (options?.shouldUpdateButtonsLoading) {
          setButtonsLoading(STATUSPAGE.DONE);
        }

        setHasFetchedData(true);

        return;
      }

      setTableLoading(STATUSPAGE.DONE);

      if (options?.shouldUpdateButtonsLoading) {
        setButtonsLoading(STATUSPAGE.DONE);
      }

      setHasFetchedData(true);

      const totalPages = Math.ceil(
        (firstPage.total || 0) / MAX_GROUPS_PAGE_LENGTH,
      );

      if (totalPages <= 1) {
        setLoadingAllGroups(false);
        return;
      }

      const remainingPages = Array.from(
        { length: totalPages - 1 },
        (_, index) => index + 2,
      );

      await Promise.all(
        remainingPages.map((page) =>
          reportService
            .getStudentsWorkloadAchievement({
              ...params,
              page,
              length: MAX_GROUPS_PAGE_LENGTH,
            })
            .then((res) => {
              setWorkloadAchievementData((prev) => ({
                total: prev.total,
                groups: [...prev.groups, ...(res.groups || [])],
              }));
            })
            .catch((err) => notifyError(err.message)),
        ),
      );

      setLoadingAllGroups(false);
    },
    [],
  );

  const requestExportStudentsWorkloadAchievement = useCallback(
    async (
      params: ExportStudentsWorkloadAchievementUseCaseInterface.Params,
    ) => {
      setButtonsLoading(STATUSPAGE.LOADING);

      await reportService
        .exportStudentsWorkloadAchievement(params)
        .then((res) => {
          if (!res?.url) {
            throw new Error('URL do relatório não foi retornada.');
          }

          if (params.type === 'pdf') {
            window.open(res.url, '_blank');
            return;
          }

          const link = document.createElement('a');
          link.href = res.url;
          link.download = 'carga-horaria-alcancada.xlsx';
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.click();
        })
        .catch((err) => notifyError(err.message))
        .finally(() => setButtonsLoading(STATUSPAGE.DONE));
    },
    [],
  );

  const validation = useFormik<StudentsWorkloadAchievementFormValues>({
    enableReinitialize: true,
    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: null,
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getToday()),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({ label: Yup.string(), value: Yup.string() })
        .nullable(),
      course: Yup.object()
        .shape({ label: Yup.string(), value: Yup.string() })
        .nullable(),
      unit: Yup.object()
        .shape({ label: Yup.string(), value: Yup.string() })
        .nullable(),
      semester: Yup.object()
        .shape({ label: Yup.string(), value: Yup.string() })
        .nullable(),
      groups: Yup.object()
        .shape({ label: Yup.string(), value: Yup.string() })
        .nullable(),
      starts: Yup.date().nullable(),
      ends: Yup.date()
        .nullable()
        .min(Yup.ref('starts'), 'Data final deve ser maior que a inicial'),
    }),
    onSubmit: (values) => {
      const params = buildParams(values);
      const type = (
        selectedSubmitBtnRef.current === SUBMIT_BUTTON.PDF ? 'pdf' : 'excel'
      ) as ExportType;

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.JSON:
          requestGetStudentsWorkloadAchievement(params, {
            shouldUpdateButtonsLoading: true,
          });
          break;
        case SUBMIT_BUTTON.PDF:
        case SUBMIT_BUTTON.EXCEL:
          requestExportStudentsWorkloadAchievement({ ...params, type });
          break;
        default:
          break;
      }
    },
  });

  return {
    validation,
    tableLoading,
    buttonsLoading,
    loadingAllGroups,
    hasFetchedData,
    selectedSubmitBtnRef,
    workloadAchievementData,
  };
};
